import { DrawerWrapper } from '@components/ui';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core';
import { Router, navigate } from '@reach/router';
import { find, isNil } from 'lodash';
import { nanoid } from 'nanoid';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import SpotdealsAllmobilUpload from 'src/containers/SpotdealsAllmobilUpload';
import { useCreateSearchAgent } from 'src/hooks/useCreateSearchAgent';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import { usePurchaseAgents } from 'src/hooks/usePurchaseAgents';
import { useSearchAgentDelete } from 'src/hooks/useSearchAgentDelete';
import { useSearchAgents } from 'src/hooks/useSearchAgents';
import { useSearchAgentUpdate } from 'src/hooks/useSearchAgentUpdate';
import { useUser } from 'src/hooks/useUser';
import { UserPermissions, useUserPermissions } from 'src/hooks/useUserPermissions';
import { isSystemAgent } from 'src/modules/system-agents';

import { AutoRefresh, AutoRefreshData } from './AutoRefresh';
import { SidebarRoutePaths } from './constants';
import { CarsTableNextFilter } from './filter';
import { transformForForm } from './filter/lib';
import { useFilter } from './filter/useFilter';
import { getAllExportableColumns } from './lib';
import { refetchIntervalState } from './state';
import { ExportColumn, TableColumnDisplaySettings, TableExport, TableView, TableViews } from './table';
import { PurchaseAgentsView } from './table/PurchaseAgentsView';
import { CarsTableNextInstance } from './types';
import { useCarsExport } from './useCarsExport';

type SidebarProps = {
  table: CarsTableNextInstance;
  refetch: () => void;
};

export const CarsTableSidebar = ({ table, refetch }: SidebarProps): JSX.Element => {
  const { t } = useTranslation();
  const { data: searchAgents, reorder } = useSearchAgents();
  const { mutate: updateSearchAgent } = useSearchAgentUpdate();
  const { mutate: deleteSearchAgent } = useSearchAgentDelete();
  const { mutate: createSearchAgent } = useCreateSearchAgent();
  const { mutate: exportCars } = useCarsExport();
  const [refetchInterval, setRefetchInterval] = useRecoilState(refetchIntervalState);
  const { setFilterSafe, watch } = useFilter();
  const filterState = watch();
  const results = table.getPageCount();
  const { data: user } = useUser();
  const authProvider = user?.user?.authenticationProvider ?? '';
  const userId = user?.user?.id ?? '';
  const purchaseAgentFlagEnabled = useFeatureFlags('purchaseAgent');
  const hasPurchaseAgentPermission = useUserPermissions(UserPermissions.PURCHASE_AGENT);
  const showPurchaseAgent = purchaseAgentFlagEnabled && hasPurchaseAgentPermission;
  const { data: purchaseAgents } = usePurchaseAgents({ enabled: showPurchaseAgent });
  const [pendingDeleteView, setPendingDeleteView] = useState<TableView | null>(null);

  const handleSubmitExport = (exportColumns: ExportColumn[]) => {
    const { sorting } = table.getState();
    exportCars({
      filter: filterState,
      sorting,
      exportColumns,
      results,
    });
    navigate(SidebarRoutePaths.root);
  };

  const handleViewChange = useCallback(
    (updatedView: TableView) => {
      const target = find(searchAgents, { id: updatedView.id });

      if (target) {
        updateSearchAgent({
          ...target,
          searchAgentDisplayName: updatedView.displayName,
          favorite: updatedView.favorite,
        });
      }
    },
    [searchAgents, updateSearchAgent],
  );

  const handleDeleteView = useCallback(
    (targetView: TableView) => {
      const hasLinkedPurchaseAgent = (purchaseAgents ?? []).some(
        (pa) => pa.linkedSearchAgent?.id?.value === targetView.id,
      );
      if (hasLinkedPurchaseAgent) {
        setPendingDeleteView(targetView);
      } else {
        deleteSearchAgent(targetView.id);
      }
    },
    [deleteSearchAgent, purchaseAgents],
  );

  const handleDuplicateView = useCallback(
    (targetView: TableView) => {
      const target = find(searchAgents, { id: targetView.id });

      if (target) {
        createSearchAgent({
          ...target,
          // @ts-expect-error
          id: nanoid(),
          searchAgentDisplayName: `${target.searchAgentDisplayName} (Kopie)`,
        });
      }
    },
    [createSearchAgent, searchAgents],
  );

  const handleChangeRefetchInterval = (data: AutoRefreshData) => {
    // only update refetch interval if active
    if (!isNil(refetchInterval)) {
      setRefetchInterval(data.intervalMs);
    }
  };

  return (
    <DrawerWrapper>
      <Router basepath="/" style={{ height: '100%' }}>
        <CarsTableNextFilter
          // @ts-expect-error
          path={SidebarRoutePaths.filter}
        />
        <TableColumnDisplaySettings
          table={table}
          columns={table.getAllLeafColumns()}
          // @ts-expect-error
          path={SidebarRoutePaths.columnSettings}
        />
        <TableViews
          views={searchAgents
            .filter((agent) => !isSystemAgent(agent.searchAgentDisplayName, authProvider, userId))
            .map((searchAgent) => ({
              id: searchAgent.id,
              displayName: searchAgent.searchAgentDisplayName,
              favorite: searchAgent.favorite,
              active: filterState?.id === searchAgent.id,
              notification: searchAgent.active,
              meta: {
                searchAgent,
              },
            }))}
          onReorder={reorder}
          onViewChange={handleViewChange}
          onDeleteView={handleDeleteView}
          onDuplicateView={handleDuplicateView}
          onActiveChange={(view) => setFilterSafe(transformForForm(view.meta.searchAgent))}
          // @ts-expect-error
          path={SidebarRoutePaths.views}
        />
        <AutoRefresh
          onSubmit={(data) => setRefetchInterval(data.intervalMs)}
          onPause={() => setRefetchInterval(null)}
          intervalMs={refetchInterval}
          isActive={!isNil(refetchInterval)}
          onChange={handleChangeRefetchInterval}
          // @ts-expect-error
          path={SidebarRoutePaths.autoRefresh}
        />
        <TableExport
          columns={getAllExportableColumns(table.getAllLeafColumns())}
          onSubmit={handleSubmitExport}
          // @ts-expect-error
          path={SidebarRoutePaths.export}
        />
        <SpotdealsAllmobilUpload
          refetchCars={refetch}
          // @ts-expect-error
          path={SidebarRoutePaths.upload}
        />
        {showPurchaseAgent && (
          <PurchaseAgentsView
            // @ts-expect-error
            path={SidebarRoutePaths.purchaseAgents}
          />
        )}
      </Router>
      <Dialog open={pendingDeleteView !== null} onClose={() => setPendingDeleteView(null)}>
        <DialogTitle>{t('searchAgentTable.deleteLinkedConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('searchAgentTable.deleteLinkedConfirmText')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDeleteView(null)} color="secondary" variant="outlined" size="small">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (pendingDeleteView) deleteSearchAgent(pendingDeleteView.id);
              setPendingDeleteView(null);
            }}
            color="primary"
            variant="contained"
            size="small"
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </DrawerWrapper>
  );
};
